import axios from 'axios';
import { API_URL, API_KEY } from '../lib/constants';
import type {
  PolicyGenerationTemplate,
  GeneratePolicyParams,
  RegenerateSectionParams,
  PolicyGenerationStreamEvent,
  CreatePolicyRecordParams,
} from '../types/policyGeneration';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 60000,
  headers: {
    'x-api-key': API_KEY,
  },
});

// Policy Generation API endpoints
const POLICY_GEN_ENDPOINTS = {
  TEMPLATES: '/api/revisr/policy-generation/templates',
  CREATE: '/api/revisr/policy-generation/create',
  GENERATE: '/api/revisr/policy-generation/generate',
  CONTENT: (id: string) => `/api/revisr/policy-generation/${id}/content`,
  STEP: (id: string) => `/api/revisr/policy-generation/${id}/step`,
  FINALIZE: (id: string) => `/api/revisr/policy-generation/${id}/finalize`,
  REGENERATE_SECTION: (id: string) => `/api/revisr/policy-generation/${id}/regenerate-section`,
  REGENERATE_DOCUMENT: (id: string) => `/api/revisr/policy-generation/${id}/regenerate-document`,
};

// Helper to build auth headers
const getHeaders = (userId?: string, orgId?: string): Record<string, string> => {
  const headers: Record<string, string> = {
    'x-api-key': API_KEY,
  };
  if (userId) headers['x-user-id'] = userId;
  if (orgId) headers['x-org-id'] = orgId;
  return headers;
};

/**
 * Get available templates for policy generation
 */
export const getTemplates = async (
  orgId: string,
  userId: string,
  search?: string
): Promise<PolicyGenerationTemplate[]> => {
  try {
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    const url = search
      ? `${POLICY_GEN_ENDPOINTS.TEMPLATES}?${params.toString()}`
      : POLICY_GEN_ENDPOINTS.TEMPLATES;

    const response = await api.get<{ templates: PolicyGenerationTemplate[] }>(url, {
      headers: getHeaders(userId, orgId),
    });
    return response.data.templates;
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

/**
 * Create a new policy generation record
 */
export const createPolicyRecord = async (
  params: CreatePolicyRecordParams
): Promise<{ id: string }> => {
  try {
    const response = await api.post<{ success: boolean; id: string }>(
      POLICY_GEN_ENDPOINTS.CREATE,
      {
        policy_name: params.policy_name,
        template_store_id: params.template_store_id,
        generation_prompt: params.generation_prompt,
        org_id: params.org_id,
        workflow_type: params.workflow_type,
        metadata: params.metadata,
      },
      { headers: getHeaders(params.user_id, params.org_id) }
    );
    return { id: response.data.id };
  } catch (error) {
    console.error('Error creating policy record:', error);
    // Detect subscription limit (403 with LIMIT_REACHED code)
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      const data = error.response.data as { code?: string; reason?: string; current?: number; limit?: number };
      if (data?.code?.includes('LIMIT_REACHED')) {
        const limitError = new Error('Policy limit reached');
        (limitError as any).isLimitReached = true;
        (limitError as any).current = data.current;
        (limitError as any).limit = data.limit;
        throw limitError;
      }
    }
    throw error;
  }
};

/**
 * Generate a policy with SSE streaming
 * Uses fetch + TextDecoder to handle streaming events
 */
export const generatePolicy = async (
  params: GeneratePolicyParams,
  onEvent: (event: PolicyGenerationStreamEvent) => void
): Promise<void> => {
  const headers = getHeaders(params.userId, params.orgId);
  headers['Content-Type'] = 'application/json';

  const response = await fetch(`${API_URL}${POLICY_GEN_ENDPOINTS.GENERATE}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      templateId: params.templateId,
      templateName: params.templateName,
      policyName: params.policyName,
      additionalPrompt: params.additionalPrompt,
      orgContext: params.orgContext,
    }),
  });

  if (!response.ok) {
    let errorMessage = `Generation request failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error || errorData.message) {
        errorMessage = errorData.error || errorData.message;
      }
    } catch {
      // ignore parse errors
    }
    onEvent({ type: 'error', message: errorMessage });
    throw new Error(errorMessage);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) return;

  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const jsonStr = line.substring(6);
            if (jsonStr.trim()) {
              const event = JSON.parse(jsonStr) as PolicyGenerationStreamEvent;
              onEvent(event);
            }
          } catch (e) {
            console.warn('Failed to parse SSE event:', e);
          }
        }
      }
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Streaming error';
    onEvent({ type: 'error', message: errorMsg });
    throw error;
  }
};

/** Placeholder used for unsaved policies; backend ignores this URL segment (log-only). */
const UNSAVED_POLICY_ID_PLACEHOLDER = 'draft';

export type RegenerateSectionResult = {
  /** 'remove' when the user's prompt asked to delete the section; 'replace' otherwise. */
  action: 'replace' | 'remove';
  /** New section markdown ('' when action is 'remove'). */
  content: string;
};

/**
 * Regenerate a single section (simple request-response, no streaming)
 */
export const regenerateSection = async (
  policyId: string | undefined,
  params: RegenerateSectionParams,
  userId?: string,
  orgId?: string,
  signal?: AbortSignal
): Promise<RegenerateSectionResult> => {
  // Unsaved policies have no id yet. The backend only logs the :id segment
  // (it regenerates from the request body), so a placeholder is safe and keeps
  // the URL well-formed instead of producing `//regenerate-section`.
  const idForUrl = policyId && policyId.trim().length > 0 ? policyId : UNSAVED_POLICY_ID_PLACEHOLDER;

  const headers = getHeaders(userId, orgId);
  headers['Content-Type'] = 'application/json';

  console.log('📡 Regenerating section:', {
    endpoint: `${API_URL}${POLICY_GEN_ENDPOINTS.REGENERATE_SECTION(idForUrl)}`,
    sectionTitle: params.sectionTitle,
  });

  const response = await fetch(
    `${API_URL}${POLICY_GEN_ENDPOINTS.REGENERATE_SECTION(idForUrl)}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        sectionIndex: params.sectionIndex,
        sectionTitle: params.sectionTitle,
        additionalPrompt: params.additionalPrompt,
        fullDocumentContext: params.fullDocumentContext,
      }),
      signal,
    }
  );

  if (!response.ok) {
    let errorMessage = `Regeneration request failed: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error || errorData.message) {
        errorMessage = errorData.error || errorData.message;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(errorMessage);
  }

  const data = await response.json();
  const action: RegenerateSectionResult['action'] = data.action === 'remove' ? 'remove' : 'replace';

  if (!data.success || (action === 'replace' && !data.content)) {
    throw new Error('Invalid response from server');
  }

  console.log('✅ Section regenerated successfully:', {
    sectionTitle: data.sectionTitle,
    action,
    contentLength: (data.content || '').length,
  });

  return { action, content: data.content || '' };
};

export type RegenerateDocumentParams = {
  /** Current full document as markdown (context for the rewrite). */
  fullDocumentContext: string;
  /** User's free-text comments/context to incorporate. */
  additionalPrompt?: string;
};

/**
 * Regenerate the entire policy document with SSE streaming.
 * Calls `handlers.onChunk` with the ACCUMULATED full text on every content/complete
 * event so the UI can render the document live. Returns the final markdown.
 */
export const regenerateDocumentStream = async (
  policyId: string | undefined,
  params: RegenerateDocumentParams,
  handlers: { onChunk?: (fullText: string) => void; signal?: AbortSignal },
  userId?: string,
  orgId?: string,
): Promise<string> => {
  const idForUrl = policyId && policyId.trim().length > 0 ? policyId : UNSAVED_POLICY_ID_PLACEHOLDER;
  const headers = getHeaders(userId, orgId);
  headers['Content-Type'] = 'application/json';

  const response = await fetch(`${API_URL}${POLICY_GEN_ENDPOINTS.REGENERATE_DOCUMENT(idForUrl)}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ additionalPrompt: params.additionalPrompt, fullDocumentContext: params.fullDocumentContext }),
    signal: handlers.signal,
  });
  if (!response.ok) {
    let msg = `Document regeneration failed: ${response.statusText}`;
    try { const e = await response.json(); if (e.error || e.message) msg = e.error || e.message; } catch { /* ignore */ }
    throw new Error(msg);
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Failed to get response stream reader');
  const decoder = new TextDecoder();
  let fullContent = '';
  let lineBuffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    lineBuffer += decoder.decode(value, { stream: true });
    const lines = lineBuffer.split('\n');
    lineBuffer = lines.pop() ?? '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      let data: Record<string, unknown>;
      try { data = JSON.parse(line.slice(6)); } catch { continue; }
      if (data.type === 'content') {
        fullContent += (data.text as string) ?? '';
        handlers.onChunk?.(fullContent);
      } else if (data.type === 'complete') {
        fullContent = (data.fullContent as string) || fullContent;
        handlers.onChunk?.(fullContent);
      } else if (data.type === 'error') {
        throw new Error((data.message as string) || 'Document regeneration failed');
      }
    }
  }
  if (!fullContent) throw new Error('Invalid response from server');
  return fullContent;
};

/**
 * Regenerate the entire policy document. Returns the new markdown.
 * Stream-backed (no onChunk) so the editor call site keeps working unchanged.
 */
export const regenerateDocument = async (
  policyId: string | undefined,
  params: RegenerateDocumentParams,
  userId?: string,
  orgId?: string,
  signal?: AbortSignal
): Promise<string> => regenerateDocumentStream(policyId, params, { signal }, userId, orgId);

/**
 * Save content (policy_markdown_url) for a policy record.
 * Optionally set current_step and status in the same DB write to avoid a
 * separate PATCH /:id/step call.
 */
export const saveContent = async (
  policyId: string,
  markdown: string,
  userId?: string,
  orgId?: string,
  options?: { current_step?: number; status?: string }
): Promise<void> => {
  try {
    await api.patch(
      POLICY_GEN_ENDPOINTS.CONTENT(policyId),
      {
        policy_markdown_url: markdown,
        ...options,
      },
      { headers: getHeaders(userId, orgId) }
    );
  } catch (error) {
    console.error('Error saving policy content:', error);
    throw error;
  }
};

/**
 * Update the current workflow step (1-4)
 */
export const updateStep = async (
  policyId: string,
  step: number,
  userId?: string,
  orgId?: string,
  status?: string
): Promise<void> => {
  try {
    const body: Record<string, unknown> = { current_step: step };
    if (status) {
      body.status = status;
    }
    await api.patch(
      POLICY_GEN_ENDPOINTS.STEP(policyId),
      body,
      { headers: getHeaders(userId, orgId) }
    );
  } catch (error) {
    console.error('Error updating policy step:', error);
    throw error;
  }
};

/**
 * Finalize a policy (set status to 'completed')
 */
export const finalize = async (
  policyId: string,
  userId?: string,
  orgId?: string
): Promise<void> => {
  try {
    await api.patch(
      POLICY_GEN_ENDPOINTS.FINALIZE(policyId),
      {},
      { headers: getHeaders(userId, orgId) }
    );
  } catch (error) {
    console.error('Error finalizing policy:', error);
    throw error;
  }
};
